// color-exempt-file — retokenisation 5728fe509 : classes dark:bg-[#hex] ajoutees pour preserver le rendu dark historique (voir memoire ui-themes)
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Share2,
  Download,
  Bell,
  Edit,
  UserPlus,
  Mail,
  QrCode,
  Star,
  MapPin,
  ChevronDown,
  Users,
  Calendar,
  Vote as VoteIcon,
  Trash2,
  MoreVertical,
  Archive,
  Lock,
  Unlock,
  Copy,
  CalendarCheck,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@core/components/ui/dropdown-menu";
import { deleteVotesByPollId } from "@core/lib/pollStorage";
import { pollService } from "@core/services/PollService";
import { usePollDeletionCascade } from "@core/hooks/usePollDeletionCascade";
import { logError } from "@core/lib/error-handling";
import { Button } from "@core/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@core/components/ui/alert-dialog";
import { useToast } from "@core/hooks/use-toast";
import { DatePollExportService } from "@core/services/DatePollExportService";
import { prepareDatePollExportData } from "@core/services/prepareDatePollExportData";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@core/contexts/AuthContext";
import { canExportFormat, type ExportFormat } from "@core/constants/quotas";
import { useFeatureFlags } from "@core/contexts/FeatureFlagsContext";
import { toIntlLocale } from "@core/lib/date-utils";
import ResultsTable, {
  type Participant as RtParticipant,
} from "@core/components/results/ResultsTable";
import ResultsModeTabs from "@core/components/results/ResultsModeTabs";
import ThresholdSegmented from "@core/components/results/ThresholdSegmented";
import ConsensusBanner from "@core/components/results/ConsensusBanner";
import PendingParticipants from "@core/components/results/PendingParticipants";
import BlockerCallout from "@core/components/results/BlockerCallout";
import WinnerBanner from "@core/components/results/WinnerBanner";
import SlotFilterChips from "@core/components/results/SlotFilterChips";
import ResultsOverview from "@core/components/results/ResultsOverview";
import MobileResults from "@core/components/results/MobileResults";
import VoteGlyphLegend from "@core/components/results/VoteGlyphLegend";
import { useSlotFilter, type SlotView } from "@core/hooks/useSlotFilter";
import { topSlotIds } from "@core/lib/insights/slotRanking";
import { topDayOf } from "@core/lib/insights/dayScores";
import { useResultsMode } from "@core/hooks/useResultsMode";
import { useShareTracking } from "@core/hooks/useShareTracking";
import { QRCodeDisplay } from "@core/components/shared/QRCodeDisplay";
import { applyConsensusFilter } from "@core/lib/insights/consensusFilter";
import { rankByEngagement } from "@core/lib/insights/engagement";
import { computeBlockers } from "@core/lib/insights/blockers";
import type { Slot, PollInsights } from "@core/lib/insights/types";
import RecurringSeriesManager from "@core/components/polls/RecurringSeriesManager";
import PendingInvitationsBanner from "@core/components/invitations/PendingInvitationsBanner";
import FixDateDialog from "@core/components/final-date/FixDateDialog";
import FinalDateCard from "@core/components/final-date/FinalDateCard";
import {
  FixDateContext,
  type OpenFixDateOptions,
} from "@core/components/final-date/FixDateContext";
import { readFinalDate, type FinalDate } from "@core/lib/final-date";
import { useFinalDateAvailable } from "@core/hooks/useFinalDateAvailable";
import { CtaTracker } from "@core/services/CtaTracker";
import { getOwnerTokenForPoll } from "@core/lib/guestIdentityService";

interface DatePollResultsProps {
  poll: any;
  options: any[];
  votes: any[];
  totalVoters?: number;
  onBack: () => void;
  onDeleteVote?: (responseId: string) => Promise<void>;
  // Vérité serveur (get_poll_results_access.is_creator), PAS un recalcul
  // local — cf. commentaire de sécurité au point d'usage plus bas.
  isCreator?: boolean;
  /**
   * Le sondage appartient-il au COMPTE connecté ? Faux pour un sondage invité
   * non rattaché, même si le visiteur est reconnu créateur par un jeton
   * d'admin (invité, ou connecté ayant reçu le lien) : `PollService.updatePoll`
   * refuse alors toute modification tant que le sondage n'est pas rattaché.
   * Pilote le bouton « Modifier ». Vrai par défaut (appelants historiques).
   */
  ownedByAccount?: boolean;
  /**
   * Panneau d'admin invité (clôturer/rouvrir, partage, encart compte),
   * construit par la page Results et rendu sous la barre d'actions.
   */
  ownerPanel?: React.ReactNode;
  /**
   * Encart affiché en haut, sous le titre et le compteur de votants, avant
   * les résultats (invitation à créer un compte de l'organisateur invité).
   */
  topBanner?: React.ReactNode;
  /** Appelé quand l'organisateur vient de fixer la date (le sondage est clos). */
  onFinalDateFixed?: (finalDate: FinalDate) => void;
}

interface ParticipantVote {
  id: string;
  name: string;
  votes: Record<string, "yes" | "maybe" | "no">;
}

export const DatePollResults: React.FC<DatePollResultsProps> = ({
  poll,
  options: rawOptions,
  votes,
  totalVoters,
  onBack,
  onDeleteVote,
  isCreator = false,
  ownedByAccount = true,
  ownerPanel,
  topBanner,
  onFinalDateFixed,
}) => {
  const { t, i18n } = useTranslation(["polls", "common", "errors"]);
  // Issue #759 — tri chronologique ; l'ordre de clic à la création n'est pas
  // une donnée métier et confond les voters quand non-monotone.
  const options = useMemo(
    () =>
      [...(rawOptions || [])].sort((a, b) =>
        String(a?.option_date || "").localeCompare(
          String(b?.option_date || ""),
        ),
      ),
    [rawOptions],
  );
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  // Modification : compte propriétaire, ou navigateur détenant le jeton de
  // propriétaire de ce sondage invité (cf. bouton « Modifier »).
  const canEditPoll =
    isCreator &&
    ((!!user && ownedByAccount !== false) || !!getOwnerTokenForPoll(poll.id));
  const { flags } = useFeatureFlags();
  const redesignV2 = flags.enableRedesignPollsV2;
  const { deletePollWithCascade } = usePollDeletionCascade();
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleteTargetName, setDeleteTargetName] = useState<string>("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [waitingInvitationsCount, setWaitingInvitationsCount] = useState(0);

  useEffect(() => {
    if (!isCreator || !poll?.id) return;
    let cancelled = false;
    import("@core/services/InvitationService").then(
      ({ getPollInvitationsWaitingCount }) => {
        getPollInvitationsWaitingCount(poll.id)
          .then((count) => {
            if (!cancelled) setWaitingInvitationsCount(count);
          })
          .catch(() => {
            // Badge non bloquant : une erreur ici masque juste le compteur.
          });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [isCreator, poll?.id]);

  // ── Fixer la date (spec S3) ─────────────────────────────────────────────
  const [fixDateOpen, setFixDateOpen] = useState(false);
  const [fixDateSlotId, setFixDateSlotId] = useState<string | null>(null);
  const [fixDateCloseWithout, setFixDateCloseWithout] = useState<
    (() => void | Promise<void>) | null
  >(null);
  const [finalDateOverride, setFinalDateOverride] = useState<FinalDate | null>(
    null,
  );
  const finalDate = finalDateOverride ?? readFinalDate(poll);
  // Migration pas encore appliquée → ancien « Terminer le sondage ».
  const finalDateAvailable = useFinalDateAvailable();

  const openFixDate = useCallback((opts: OpenFixDateOptions = {}) => {
    setFixDateSlotId(opts.slotId ?? null);
    setFixDateCloseWithout(() => opts.onCloseWithoutDate ?? null);
    setFixDateOpen(true);
  }, []);

  const handleClosePoll = () => {
    if (!finalDateAvailable) {
      if (!window.confirm(t("polls:results_close_poll_confirm"))) return;
      void closePollWithoutDate();
      return;
    }
    openFixDate({ onCloseWithoutDate: () => closePollWithoutDate() });
  };

  const closePollWithoutDate = async () => {
    try {
      await pollService.updatePoll(poll.id, { status: "closed" });
      toast({
        title: t("polls:results_poll_closed_title"),
        description: t("polls:results_poll_closed_desc"),
      });
      navigate("/tableau-de-bord");
    } catch (error) {
      logError(error, {
        component: "DatePollResults",
        operation: "closePoll",
      });
      toast({
        title: t("errors:generic"),
        description: t("polls:results_close_poll_error"),
        variant: "destructive",
      });
    }
  };

  const handleReopenPoll = async () => {
    try {
      await pollService.updatePoll(poll.id, { status: "active" });
      toast({ title: t("polls:guest_admin.reopened_toast") });
      navigate("/tableau-de-bord");
    } catch (error) {
      logError(error, {
        component: "DatePollResults",
        operation: "reopenPoll",
      });
      toast({
        title: t("errors:generic"),
        description: t("polls:guest_admin.status_error"),
        variant: "destructive",
      });
    }
  };

  const handleArchivePoll = async () => {
    try {
      await pollService.updatePoll(poll.id, { status: "archived" });
      toast({
        title: t("polls:results_poll_archived_title"),
        description: t("polls:results_poll_archived_desc"),
      });
      navigate("/tableau-de-bord");
    } catch (error) {
      logError(error, {
        component: "DatePollResults",
        operation: "archivePoll",
      });
      toast({
        title: t("errors:generic"),
        description: t("polls:results_archive_poll_error"),
        variant: "destructive",
      });
    }
  };

  const handleDuplicatePoll = async () => {
    try {
      const dup = await pollService.duplicatePoll(poll as any);
      toast({
        title: t("polls:results_poll_duplicated_title"),
        description: t("polls:results_poll_duplicated_desc", {
          title: dup.title,
        }),
      });
      navigate(`/workspace/date?edit=${dup.id}`);
    } catch (error) {
      logError(error, {
        component: "DatePollResults",
        operation: "duplicatePoll",
      });
      toast({
        title: t("errors:generic"),
        description: t("polls:results_duplicate_poll_error"),
        variant: "destructive",
      });
    }
  };

  const handleDeletePoll = async () => {
    if (!window.confirm(t("polls:results_delete_poll_confirm"))) return;
    try {
      deleteVotesByPollId(poll.id);
      const result = await deletePollWithCascade(poll.id, {
        deleteConversation: true,
      });
      if (!result.success) {
        throw new Error(result.error || "Failed to delete poll");
      }
      toast({
        title: t("polls:results_poll_deleted_title"),
        description: t("polls:results_poll_deleted_desc"),
      });
      navigate("/tableau-de-bord");
    } catch (error) {
      logError(error, {
        component: "DatePollResults",
        operation: "deletePoll",
      });
      toast({
        title: t("errors:generic"),
        description:
          error instanceof Error
            ? error.message
            : t("polls:results_delete_poll_error"),
        variant: "destructive",
      });
    }
  };

  // SÉCURITÉ (2026-08-19) : `isCreator` est désormais un prop reçu du
  // serveur (get_poll_results_access.is_creator, cf. Results.tsx), jamais
  // recalculé ici. L'ancien recalcul local reposait sur la présence de
  // `guest_fingerprint` dans l'objet poll — or ce champ décrit le SONDAGE
  // (son créateur d'origine), pas le VISITEUR courant ; un `!!guestFingerprint`
  // truthy ne prouvait donc rien sur qui regarde la page. Combiné à un
  // fingerprint navigateur déterministe (devinable/collisionnel, cf.
  // migration 20260819103000_harden_guest_poll_ownership.sql), n'importe
  // quel votant pouvait se voir accorder les actions créateur.

  // Gérer la fermeture du dropdown au clic extérieur
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!showExportMenu) return;

      const target = event.target as HTMLElement;
      // Check if click is outside the dropdown container
      if (
        !target.closest(".export-dropdown") &&
        !target.closest("[data-export-menu]")
      ) {
        setShowExportMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showExportMenu]);

  // Transformer les votes en structure participant × date
  const participantVotes = useMemo<ParticipantVote[]>(() => {
    return votes.map((vote) => ({
      id: vote.id ?? "",
      name: vote.voter_name || t("polls:results_anonymous"),
      votes: vote.selections || {},
    }));
  }, [votes, t]);

  // Calculer les statistiques par date
  const dateStats = useMemo(() => {
    return options.map((option) => {
      const yesCount = votes.filter(
        (v) => v.selections[option.id] === "yes",
      ).length;
      const maybeCount = votes.filter(
        (v) => v.selections[option.id] === "maybe",
      ).length;
      const noCount = votes.filter(
        (v) => v.selections[option.id] === "no",
      ).length;
      const totalVotes = votes.length;
      const score =
        totalVotes > 0 ? (yesCount + maybeCount * 0.5) / totalVotes : 0;

      return {
        optionId: option.id,
        date: option.option_date,
        date_group_label: (option as any).date_group_label || null,
        time_label: (option as any).time_label || null,
        yesCount,
        maybeCount,
        noCount,
        totalVotes,
        score,
      };
    });
  }, [options, votes]);

  // Adaptateurs pour la ResultsTable V2 (redesign flag) — dérivés des
  // mêmes données que `dateStats` pour zéro coût réseau supplémentaire.
  const v2Slots = useMemo<Slot[]>(
    () =>
      options.map((opt, i) => ({
        id: opt.id,
        index: i,
        primaryDate: opt.option_date,
        dates: Array.isArray((opt as any).date_group)
          ? (opt as any).date_group
          : [opt.option_date],
        // Propage le label groupé (ex "Week-end du 12-13 sep") produit par
        // buildPollOptions — sans lui, groupByDay/dayScores retombent sur
        // primaryDate et perdent le groupement week-end à l'affichage.
        groupLabel: (opt as any).date_group_label ?? undefined,
        // Propage les time slots produits par buildPollOptions pour que
        // ResultsTable.slotHeaderLabel affiche l'heure (ex "12h - 13h") au
        // lieu de la date — sinon fallback formatDate qui répète la date
        // déjà visible dans le day-banner row.
        timeSlots: Array.isArray(opt.time_slots) ? opt.time_slots : null,
      })),
    [options],
  );
  const v2Participants = useMemo<RtParticipant[]>(() => {
    return votes.map((v: any) => ({
      id: v.id ?? `${v.voter_name ?? "anon"}-${v.created_at ?? ""}`,
      name: v.voter_name || t("polls:results_anonymous"),
      votes: (v.selections || {}) as Record<string, "yes" | "maybe" | "no">,
      isYou:
        (!!user?.id && v.voter_id === user.id) || !!(v as any).is_own_response,
    }));
  }, [votes, user, t]);
  const v2Insights = useMemo<PollInsights>(() => {
    const scores = dateStats.map((s) => ({
      slotId: s.optionId,
      yes: s.yesCount,
      maybe: s.maybeCount,
      no: s.noCount,
      empty: Math.max(0, s.totalVotes - s.yesCount - s.maybeCount - s.noCount),
      total: s.totalVotes,
    }));
    let winner: string | null = null;
    let maxScore = -1;
    let maxYes = 0;
    for (const s of scores) {
      const score = s.total > 0 ? (s.yes + s.maybe * 0.5) / s.total : 0;
      if (score > maxScore) {
        maxScore = score;
        winner = s.slotId;
        maxYes = s.yes;
      }
    }
    return {
      scores,
      winnerSlotId: winner,
      maxYes,
      participantsCount: votes.length,
    };
  }, [dateStats, votes.length]);

  // Créneau correspondant à la date déjà fixée (pour « Changer la date »).
  const finalDateSlotId = useMemo<string | null>(() => {
    if (!finalDate) return null;
    const pad = (n: number) => n.toString().padStart(2, "0");
    const match = v2Slots.find((s) => {
      if (s.primaryDate !== finalDate.date) return false;
      const ts = s.timeSlots?.[0];
      if (!finalDate.start) return !ts;
      return !!ts && `${pad(ts.hour)}:${pad(ts.minute)}` === finalDate.start;
    });
    return match?.id ?? v2Slots.find((s) => s.primaryDate === finalDate.date)?.id ?? null;
  }, [finalDate, v2Slots]);

  // Trouver les meilleurs créneaux (top 2-3)
  const topSlots = useMemo(() => {
    const sorted = [...dateStats]
      .filter((s) => s.score > 0)
      .sort((a, b) => b.score - a.score);
    return sorted.slice(0, Math.min(3, sorted.length));
  }, [dateStats]);

  // Meilleur créneau pour la carte recommandation
  const bestSlot = topSlots[0];

  // Fonction pour obtenir la couleur selon le taux de disponibilité.
  // Échelle d'intensité continue (heatmap), pas une couleur de statut
  // discrète — catégorie à part, comme les palettes de graphique
  // (AnalyticsCharts.tsx) ; hors périmètre du chantier thèmes.
  const getAvailabilityColor = (
    yesCount: number,
    totalVotes: number,
  ): string => {
    if (totalVotes === 0) return "#757575"; // Gris - pas de réponse // color-exempt
    const ratio = yesCount / totalVotes;
    if (ratio >= 0.8) return "#1b5e20"; // Vert très foncé // color-exempt
    if (ratio >= 0.6) return "#2e7d32"; // Vert foncé // color-exempt
    if (ratio >= 0.4) return "#43a047"; // Vert moyen // color-exempt
    if (ratio >= 0.2) return "#81c784"; // Vert clair // color-exempt
    return "#c8e6c9"; // Vert très clair // color-exempt
  };

  // Formater la date
  const formatDate = (dateStr: string): string => {
    const [year, month, day] = dateStr.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString(toIntlLocale(i18n.language), {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  };

  // Fonction utilitaire pour préparer les données d'export
  const prepareExportData = () => {
    return prepareDatePollExportData(
      {
        id: poll.id,
        title: poll.title,
        description: poll.description,
        created_at: poll.created_at,
      },
      options.map((opt) => ({ id: opt.id, option_date: opt.option_date })),
      votes.map((vote) => ({
        id: vote.id,
        voter_name: vote.voter_name,
        voter_email: vote.voter_email,
        selections: vote.selections,
        created_at: vote.created_at,
      })),
    );
  };

  // Fonction utilitaire pour télécharger un fichier
  const downloadFile = (
    content: string | Blob,
    filename: string,
    mimeType: string,
  ) => {
    const blob =
      content instanceof Blob
        ? content
        : new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Handlers pour les boutons d'action
  const handleExportCSV = async () => {
    try {
      const exportData = prepareExportData();
      const content = DatePollExportService.exportToCSV(exportData);
      const filename = `${poll.title.replace(/[^a-z0-9]/gi, "_")}_results.csv`;
      // exportToCSV already prefixes a UTF-8 BOM (EF BB BF). Do not prefix
      // again here or the file starts with two BOMs and Pandas/csv.py read
      // the first column header as U+FEFF + "Date" instead of "Date".
      downloadFile(content, filename, "text/csv;charset=utf-8");

      toast({
        title: t("polls:results_export_success_title"),
        description: t("polls:results_export_success_csv"),
      });
      setShowExportMenu(false);
    } catch (error) {
      toast({
        title: t("polls:results_export_error_title"),
        description: t("polls:results_export_error_csv"),
        variant: "destructive",
      });
    }
  };

  const handleExportJSON = async () => {
    try {
      const exportData = prepareExportData();
      const content = DatePollExportService.exportToJSON(exportData);
      const filename = `${poll.title.replace(/[^a-z0-9]/gi, "_")}_results.json`;
      downloadFile(content, filename, "application/json");

      toast({
        title: t("polls:results_export_success_title"),
        description: t("polls:results_export_success_json"),
      });
      setShowExportMenu(false);
    } catch (error) {
      toast({
        title: t("polls:results_export_error_title"),
        description: t("polls:results_export_error_json"),
        variant: "destructive",
      });
    }
  };

  const handleExportMarkdown = async () => {
    try {
      const exportData = prepareExportData();
      const content = DatePollExportService.exportToMarkdown(exportData);
      const filename = `${poll.title.replace(/[^a-z0-9]/gi, "_")}_results.md`;
      downloadFile(content, filename, "text/markdown");

      toast({
        title: t("polls:results_export_success_title"),
        description: t("polls:results_export_success_markdown"),
      });
      setShowExportMenu(false);
    } catch (error) {
      toast({
        title: t("polls:results_export_error_title"),
        description: t("polls:results_export_error_markdown"),
        variant: "destructive",
      });
    }
  };

  const handleExportPDF = async () => {
    try {
      const exportData = prepareExportData();
      const content = await DatePollExportService.exportToPDF(exportData);
      const filename = `${poll.title.replace(/[^a-z0-9]/gi, "_")}_results.pdf`;
      downloadFile(content, filename, "application/pdf");

      toast({
        title: t("polls:results_export_success_title"),
        description: t("polls:results_export_success_pdf"),
      });
      setShowExportMenu(false);
    } catch (error) {
      toast({
        title: t("polls:results_export_error_title"),
        description: t("polls:results_export_error_pdf"),
        variant: "destructive",
      });
    }
  };

  const handleSendReminders = () => {
    toast({
      title: t("polls:results_reminders_sent_title"),
      description: t("polls:results_feature_in_development"),
    });
  };

  const handleEdit = () => {
    // Naviguer vers la page d'édition du sondage
    if (poll?.id) {
      navigate(`/poll/${poll.id}/edit`);
    } else {
      toast({
        title: t("errors:generic"),
        description: t("polls:results_edit_poll_error"),
        variant: "destructive",
      });
    }
  };

  const handleRemindNonRespondents = () => {
    toast({
      title: t("polls:results_remind_non_respondents_title"),
      description: t("polls:results_feature_in_development"),
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId || !onDeleteVote) return;
    setIsDeleting(true);
    try {
      await onDeleteVote(deleteTargetId);
      toast({ title: t("polls:results_vote_deleted_title") });
    } catch {
      toast({
        title: t("errors:generic"),
        description: t("polls:results_delete_vote_error"),
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setDeleteTargetId(null);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: t("polls:results_share_title", { title: poll.title }),
          text: t("polls:results_share_text", { title: poll.title }),
          url,
        });
        if (isCreator) CtaTracker.trackShared(poll.id, "native");
        return;
      } catch (error) {
        if ((error as Error)?.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      if (isCreator) CtaTracker.trackShared(poll.id, "copy");
      toast({
        title: t("polls:results_link_copied_title"),
        description: t("polls:results_link_copied_desc"),
      });
    } catch {
      toast({
        title: t("errors:generic"),
        description: t("polls:results_link_copy_error"),
        variant: "destructive",
      });
    }
  };

  return (
    <FixDateContext.Provider
      value={isCreator && finalDateAvailable ? openFixDate : null}
    >
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="min-h-screen overflow-x-hidden bg-background dark:bg-[#0a0a0a] pb-4 space-y-4"
      data-testid="results-container"
    >
      <div className="max-w-4xl mx-auto px-4">
        {/* Header avec Résultats tout en haut */}
        <div className="bg-background dark:bg-[#0a0a0a] py-3">
          {/* Ligne du haut: Résultats + actions */}
          <div className="flex items-center justify-between px-4">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onBack}
              aria-label={t("polls:results.back")}
              className="p-2 hover:bg-accent dark:hover:bg-[#2a2a2a] rounded-full"
            >
              <ArrowLeft className="h-5 w-5 text-muted-foreground" />
            </Button>

            <h1 className="text-lg font-bold text-foreground">
              {t("polls:results.title")}
            </h1>

            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setShowQR(!showQR)}
                aria-label={t(
                  showQR
                    ? "polls:poll_actions.hide_qr_code"
                    : "polls:poll_actions.show_qr_code",
                )}
                aria-pressed={showQR}
                className={`p-2 rounded-full ${showQR ? "bg-primary/20 text-primary" : "hover:bg-accent dark:hover:bg-[#2a2a2a] text-muted-foreground"}`}
              >
                <QrCode className="h-5 w-5" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleShare}
                aria-label={t("polls:results.shareResults")}
                className="p-2 hover:bg-accent dark:hover:bg-[#2a2a2a] rounded-full"
              >
                <Share2 className="h-5 w-5 text-muted-foreground" />
              </Button>
              {isCreator && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate(`/poll/${poll.slug}/invitations`)}
                  aria-label={t("polls:results.resendInvitations")}
                  className="relative p-2 hover:bg-accent dark:hover:bg-[#2a2a2a] rounded-full"
                  data-testid="results-resend-invitations"
                >
                  <Mail className="h-5 w-5 text-muted-foreground" />
                  {waitingInvitationsCount > 0 && (
                    <span
                      className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-semibold leading-none"
                      data-testid="results-resend-invitations-badge"
                    >
                      {waitingInvitationsCount > 9
                        ? "9+"
                        : waitingInvitationsCount}
                    </span>
                  )}
                </Button>
              )}
            </div>
          </div>
          {showQR && (
            <div className="flex justify-center pt-3 pb-1">
              <QRCodeDisplay
                url={`${window.location.origin}/poll/${poll.slug}/vote`}
                size={150}
                label={t("polls:results_scan_to_vote")}
              />
            </div>
          )}
        </div>

        {/* Titre et informations du sondage */}
        <div className="text-center mb-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground leading-tight mb-2">
            {poll.title}
          </h2>
          {poll.description && (
            <p className="text-muted-foreground text-sm mt-1">
              {poll.description}
            </p>
          )}

          {/* Lieu de l'événement */}
          {/* `location` : anciens sondages publiés via la modale de pré-publication */}
          {(poll.settings?.eventLocation || poll.settings?.location) && (
            <div className="flex items-center justify-center gap-2 mt-3 text-sm text-muted-foreground">
              <MapPin className="w-4 h-4" />
              <span>
                {poll.settings.eventLocation || poll.settings.location}
              </span>
            </div>
          )}

          {/* Invités qui n'ont pas encore voté : l'action est ici, sous le
              compteur de votants, et plus seulement derrière l'icône de la
              barre d'outils (qui reste, en accès secondaire). */}
          {isCreator && poll?.id && (
            <PendingInvitationsBanner
              pollId={poll.id}
              invitationsHref={`/poll/${poll.slug}/invitations`}
              onNavigate={navigate}
              audienceMode={(poll.settings as any)?.audienceMode}
            />
          )}

          {/* Informations sous le titre */}
          <div className="flex justify-center items-center gap-6 mt-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span>
                {t("polls:results_voters_count", {
                  count: totalVoters || votes.length,
                })}
              </span>
            </div>
            {poll.expires_at && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>
                  {t("polls:results_expires_on", {
                    date: new Date(poll.expires_at).toLocaleDateString(
                      toIntlLocale(i18n.language),
                      {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      },
                    ),
                  })}
                </span>
              </div>
            )}
          </div>
        </div>

        {topBanner}

        {finalDate && poll?.id && (poll?.status === "closed" || finalDateOverride) && (
          <div className="mb-4 space-y-2" data-testid="results-final-date">
            <FinalDateCard
              poll={{ id: poll.id, title: poll.title, slug: poll.slug }}
              finalDate={finalDate}
              variant={isCreator ? "owner" : "voter"}
            />
            {isCreator && finalDateAvailable && (
              <div className="text-center">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => openFixDate({ slotId: finalDateSlotId })}
                  data-testid="results-change-date"
                >
                  <CalendarCheck className="h-4 w-4 mr-2" />
                  {t("polls:final_date.change_date")}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Créneau Recommandé Compact — masqué quand V2 actif (WinnerBanner prend le relais) */}
        {!redesignV2 && bestSlot && bestSlot.yesCount > 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-accent dark:bg-[#2a2a2a] rounded-lg border border-border p-3 flex items-center gap-3"
          >
            <Star className="h-4 w-4 text-warning fill-warning" />
            <span className="text-foreground font-medium">
              {bestSlot.date_group_label || formatDate(bestSlot.date)}
              {bestSlot.time_label && (
                <span className="ml-2 text-muted-foreground text-sm font-normal">
                  {bestSlot.time_label}
                </span>
              )}
            </span>
            <span className="text-success text-sm">
              {bestSlot.yesCount}/{bestSlot.totalVotes} (
              {Math.round(bestSlot.score * 100)}%)
            </span>
          </motion.div>
        )}

        {/* Tableau Matriciel — V2 redesign behind flag */}
        {redesignV2 && (
          <div className="mt-4">
            <ResultsTableV2Section
              pollId={poll?.id ?? ""}
              slots={v2Slots}
              participants={v2Participants}
              insights={v2Insights}
              onFinalize={
                isCreator && finalDateAvailable && poll?.status === "active"
                  ? (slotId) => openFixDate({ slotId })
                  : undefined
              }
            />
          </div>
        )}
        {!redesignV2 && (
          <div className="bg-card dark:bg-[#1e1e1e] rounded-2xl border border-border p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-foreground">
                {t("polls:available")}
              </h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="text-left text-sm font-semibold text-muted-foreground p-2 border-b border-border">
                      {t("polls:results_participant")}
                    </th>
                    {options.map((option) => {
                      const isTopSlot = topSlots.some(
                        (ts) => ts.optionId === option.id,
                      );
                      return (
                        <th
                          key={option.id}
                          className="text-center text-sm font-semibold text-muted-foreground p-2 border-b border-border min-w-[50px] sm:min-w-[80px]"
                        >
                          <div className="flex flex-col items-center gap-1">
                            {isTopSlot && (
                              <Star className="h-4 w-4 text-foreground fill-foreground" />
                            )}
                            <span className="text-muted-foreground">
                              {(option as any).date_group_label ||
                                formatDate(option.option_date)}
                            </span>
                            {(option as any).time_label && (
                              <span className="text-xs font-medium text-foreground">
                                {(option as any).time_label}
                              </span>
                            )}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {participantVotes.map((participant, idx) => (
                    <tr key={idx} className="group border-b border-border/50">
                      <td className="text-sm text-foreground p-2 font-medium">
                        <div className="flex items-center gap-2">
                          <span>{participant.name}</span>
                          {isCreator && onDeleteVote && participant.id && (
                            <button
                              data-testid={`delete-vote-${idx}`}
                              onClick={() => {
                                setDeleteTargetId(participant.id);
                                setDeleteTargetName(participant.name);
                              }}
                              className="opacity-0 group-hover:opacity-100 hover:opacity-100 text-destructive hover:text-destructive/80 transition-opacity p-0.5 rounded"
                              title={t("polls:results_delete_vote_tooltip")}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                      {options.map((option) => {
                        const vote = participant.votes[option.id];
                        // Gris par défaut (pas de réponse) — même échelle
                        // d'intensité que getAvailabilityColor, hors périmètre.
                        let bgColor = "#757575"; // color-exempt

                        if (vote === "yes") {
                          const stats = dateStats.find(
                            (s) => s.optionId === option.id,
                          );
                          bgColor = stats
                            ? getAvailabilityColor(
                                stats.yesCount,
                                stats.totalVotes,
                              )
                            : "#2e7d32"; // color-exempt
                        } else if (vote === "maybe") {
                          bgColor = "hsl(var(--warning))";
                        } else if (vote === "no") {
                          bgColor = "hsl(var(--destructive))";
                        }

                        return (
                          <td key={option.id} className="p-2">
                            <div
                              className="w-full h-12 rounded-lg transition-all"
                              style={{ backgroundColor: bgColor }}
                              title={
                                vote === "yes"
                                  ? t("polls:available")
                                  : vote === "maybe"
                                    ? t("polls:maybe")
                                    : vote === "no"
                                      ? t("polls:unavailable")
                                      : t("polls:results_no_response")
                              }
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Légende Simplifiée */}
            <div className="mt-6 pt-4 border-t border-border">
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-success rounded" />
                  <span>{t("polls:available")}</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-warning rounded" />
                  <span>{t("polls:maybe")}</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-destructive rounded" />
                  <span>{t("polls:unavailable")}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Actions Simplifiées */}
        <div className="flex flex-wrap gap-2 p-4">
          <div className="relative export-dropdown flex-1 basis-[140px] min-w-0">
            <Button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="w-full bg-accent dark:bg-[#2a2a2a] hover:bg-muted dark:hover:bg-[#3a3a3a] text-foreground border border-border flex items-center justify-center gap-2"
            >
              <Download className="h-4 w-4 shrink-0" />
              <span className="truncate">{t("polls:results_export")}</span>
              <ChevronDown className="h-4 w-4 shrink-0" />
            </Button>

            {showExportMenu && (
              <div
                className="absolute top-full left-0 mt-2 w-48 bg-accent dark:bg-[#2a2a2a] border border-border rounded-lg shadow-lg z-50"
                data-export-menu
              >
                {(
                  [
                    ["CSV", handleExportCSV],
                    ["JSON", handleExportJSON],
                    ["Markdown", handleExportMarkdown],
                    ["PDF", handleExportPDF],
                  ] as [ExportFormat, () => void][]
                ).map(([format, run]) => {
                  const allowed = canExportFormat(profile, format);
                  return (
                    <button
                      key={format}
                      // Le format verrouillé reste VISIBLE et cliquable. Le
                      // masquer priverait l'utilisateur de l'information qu'il
                      // existe — et c'est précisément l'instant où il en a
                      // besoin qui fait le meilleur point d'entrée vers l'offre.
                      onClick={() => {
                        setShowExportMenu(false);
                        if (allowed) {
                          run();
                        } else {
                          navigate("/pricing");
                        }
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-muted dark:hover:bg-[#3a3a3a] text-foreground flex items-center justify-between gap-2"
                    >
                      <span
                        className={
                          allowed ? undefined : "text-muted-foreground"
                        }
                      >
                        {format}
                      </span>
                      {!allowed && (
                        <Lock
                          className="h-3.5 w-3.5 text-muted-foreground shrink-0"
                          aria-label={t("polls:results_export_locked")}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <Button
            onClick={() => navigate(`/poll/${poll.slug || poll.id}`)}
            className="flex-1 basis-[140px] min-w-0 bg-primary hover:bg-primary/90 text-primary-foreground flex items-center justify-center gap-2"
          >
            <VoteIcon className="h-4 w-4 shrink-0" />
            <span className="truncate">
              {v2Participants.some((p) => p.isYou)
                ? t("polls:results_edit_my_vote")
                : t("polls:vote.title")}
            </span>
          </Button>
          {/* « Modifier », même éditeur et même lien pour deux créateurs :
              - le compte propriétaire (sondage rattaché à un compte) ;
              - le créateur INVITÉ, sur le navigateur qui détient le jeton de
                propriétaire de CE sondage (2026-10-02, RPC update_guest_poll :
                la base vérifie le jeton). Avant, il n'avait aucune voie et
                recréait son sondage (enquête A7).
              Un autre navigateur, sans le jeton, ne voit pas le bouton — et la
              base refuserait de toute façon. */}
          {canEditPoll && (
            <Button
              data-testid="results-edit-poll"
              onClick={() => navigate(`/workspace/date?edit=${poll.id}`)}
              className="flex-1 basis-[120px] min-w-0 bg-accent dark:bg-[#2a2a2a] hover:bg-muted dark:hover:bg-[#3a3a3a] text-foreground border border-border flex items-center justify-center gap-2"
            >
              <Edit className="h-4 w-4 shrink-0" />
              <span className="truncate">{t("common:edit")}</span>
            </Button>
          )}
          {isCreator && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="poll-actions-menu"
                  className="bg-accent dark:bg-[#2a2a2a] hover:bg-muted dark:hover:bg-[#3a3a3a] text-foreground border border-border flex items-center justify-center px-3"
                  aria-label={t("polls:results_more_actions")}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {/* Dupliquer ouvre la copie en modification : même limite que
                    le bouton « Modifier », donc réservé aux créateurs connectés. */}
                {user && (
                  <DropdownMenuItem onClick={handleDuplicatePoll}>
                    <Copy className="h-4 w-4 mr-2" />
                    {t("polls:results_duplicate")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleArchivePoll}>
                  <Archive className="h-4 w-4 mr-2" />
                  {t("polls:results_archive")}
                </DropdownMenuItem>
                {/* Sans compte, clôturer/rouvrir passe par le panneau d'admin
                    invité (ownerPanel), qui reste sur la page. */}
                {user && poll?.status !== "closed" && (
                  <DropdownMenuItem
                    onClick={handleClosePoll}
                    data-testid="results-fix-date"
                  >
                    {finalDateAvailable ? (
                      <CalendarCheck className="h-4 w-4 mr-2" />
                    ) : (
                      <Lock className="h-4 w-4 mr-2" />
                    )}
                    {finalDateAvailable
                      ? t("polls:final_date.fix_date_action")
                      : t("polls:results_close_poll")}
                  </DropdownMenuItem>
                )}
                {user && poll?.status === "closed" && (
                  <DropdownMenuItem onClick={handleReopenPoll}>
                    <Unlock className="h-4 w-4 mr-2" />
                    {t("polls:guest_admin.reopen_poll")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleDeletePoll}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t("common:delete")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {ownerPanel}

        {/* Gestion série récurrente (créateur uniquement) */}
        {isCreator && poll?.id && <RecurringSeriesManager pollId={poll.id} />}

        {/* Message si aucun vote */}
        {votes.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-12"
          >
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {t("polls:results_no_votes_title")}
            </h3>
            <p className="text-muted-foreground">
              {t("polls:results_no_votes_desc")}
            </p>
          </motion.div>
        )}
      </div>

      {/* Modal : confirmation suppression d'un vote */}
      <AlertDialog
        open={!!deleteTargetId}
        onOpenChange={(open) => {
          if (!open) setDeleteTargetId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("polls:results_delete_vote_dialog_title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              <Trans
                i18nKey="polls:results_delete_vote_dialog_desc"
                values={{ name: deleteTargetName }}
                components={{ strong: <strong /> }}
              />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              {t("common:cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-vote"
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              {isDeleting ? t("polls:results_deleting") : t("common:delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {isCreator && poll?.id && (
        <FixDateDialog
          open={fixDateOpen}
          onOpenChange={setFixDateOpen}
          poll={{ id: poll.id, title: poll.title }}
          slots={v2Slots}
          insights={v2Insights}
          initialSlotId={fixDateSlotId ?? finalDateSlotId}
          onCloseWithoutDate={fixDateCloseWithout ?? undefined}
          onFixed={(fd) => {
            setFinalDateOverride(fd);
            onFinalDateFixed?.(fd);
          }}
        />
      )}
    </motion.div>
    </FixDateContext.Provider>
  );
};

/** V2 redesign — Mode tabs + sticky-col matrix table. */
const ResultsTableV2Section: React.FC<{
  pollId: string;
  slots: Slot[];
  participants: RtParticipant[];
  insights: PollInsights;
  /** Bouton « Valider ce créneau » du bandeau (organisateur, sondage ouvert). */
  onFinalize?: (slotId: string) => void;
}> = ({ pollId, slots, participants, insights, onFinalize }) => {
  const { t } = useTranslation(["polls", "common", "errors"]);
  const { mode, setMode, threshold, setThreshold } = useResultsMode({
    slotCount: slots.length,
    participantsCount: insights.participantsCount,
  });
  const consensus = useMemo(
    () =>
      mode === "consensus"
        ? applyConsensusFilter(slots, insights, threshold)
        : null,
    [mode, slots, insights, threshold],
  );
  const visibleSlotIds = consensus?.visibleSlotIds;
  const hiddenCount = consensus?.hiddenSlotIds.length ?? 0;

  const engagement = useMemo(
    () =>
      mode === "engagement" ? rankByEngagement(participants, slots) : null,
    [mode, participants, slots],
  );
  const nearMisses = useMemo(
    () =>
      mode === "blockers"
        ? computeBlockers(
            slots,
            insights,
            participants.map((p) => ({
              participantId: p.id,
              name: p.name,
              votes: p.votes,
            })),
          )
        : [],
    [mode, slots, insights, participants],
  );
  const slotById = useMemo(() => {
    const map = new Map<string, Slot>();
    for (const s of slots) map.set(s.id, s);
    return map;
  }, [slots]);
  const orderedParticipants =
    mode === "engagement" && engagement
      ? // Active participants only in the table (pending live in their section)
        engagement.active.map((r) => {
          const original = participants.find((p) => p.id === r.id)!;
          return original;
        })
      : participants;
  const share = useShareTracking({ pollId });
  const { toast } = useToast();
  const copyShareLink = useCallback(async () => {
    try {
      const url = typeof window !== "undefined" ? window.location.href : "";
      await navigator.clipboard.writeText(url);
      toast({
        title: t("polls:results_link_copied_title"),
        description: t("polls:results_link_copied_share_desc"),
      });
    } catch {
      toast({
        title: t("polls:results_link_copy_failed_title"),
        description: t("polls:results_link_copy_failed_desc"),
        variant: "destructive",
      });
    }
  }, [toast, t]);
  const copyLinkFor = useCallback(
    async (participantId: string) => {
      await copyShareLink();
      share.markShared(participantId);
    },
    [copyShareLink, share],
  );
  // Lot D : filtre de slots (Top N / Tous / Jour précis) persisté en URL.
  // TOP_N dynamique : min(8, ceil(slots/2)) — évite le label "Top 8" trompeur
  // quand on a 10 slots (masquerait seulement 2). Source unique avec SlotFilterChips.
  const TOP_N = React.useMemo(
    () => Math.min(8, Math.ceil(slots.length / 2)),
    [slots.length],
  );
  // Default = "top" seulement si le filtre apporte une vraie réduction.
  const defaultView: SlotView = React.useMemo(
    () => (TOP_N < slots.length ? { kind: "top" } : { kind: "all" }),
    [slots.length, TOP_N],
  );
  const { view: slotView, setView: setSlotView } = useSlotFilter(defaultView);
  const topDay = React.useMemo(
    () => topDayOf(slots, insights),
    [slots, insights],
  );
  const viewVisibleSlotIds = React.useMemo<Set<string> | null>(() => {
    if (slotView.kind === "top") return topSlotIds(slots, insights, TOP_N);
    if (slotView.kind === "day") {
      const ids = new Set<string>();
      for (const s of slots) if (s.primaryDate === slotView.date) ids.add(s.id);
      return ids;
    }
    return null; // "all"
  }, [slotView, slots, insights]);
  // Intersection avec le filtre consensus si actif. Sinon le filtre view seul.
  const combinedVisibleSlotIds = React.useMemo<Set<string> | undefined>(() => {
    if (!viewVisibleSlotIds && !visibleSlotIds) return undefined;
    if (!viewVisibleSlotIds) return visibleSlotIds ?? undefined;
    if (!visibleSlotIds) return viewVisibleSlotIds;
    const inter = new Set<string>();
    for (const id of viewVisibleSlotIds)
      if (visibleSlotIds.has(id)) inter.add(id);
    return inter;
  }, [viewVisibleSlotIds, visibleSlotIds]);

  // Détection mobile (< 640px = breakpoint sm Tailwind) — pattern A
  // remplace complètement le layout desktop.
  const [isMobile, setIsMobile] = React.useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 639px)").matches;
  });
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 639px)");
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const [overviewOpen, setOverviewOpen] = React.useState(false);
  if (isMobile && !overviewOpen) {
    return (
      <div className="space-y-3 min-w-0" data-testid="results-v2-section">
        <MobileResults
          slots={slots}
          participants={participants}
          insights={insights}
          mode={mode}
          onChangeMode={setMode}
          threshold={threshold}
          onChangeThreshold={setThreshold}
          consensus={consensus}
          engagement={engagement}
          nearMisses={nearMisses}
          slotById={slotById}
          isShared={share.isShared}
          onCopyLink={(id) => {
            void copyLinkFor(id);
          }}
          onCopyShareLink={() => {
            void copyShareLink();
          }}
        />
      </div>
    );
  }
  if (overviewOpen) {
    return (
      <div className="space-y-3 min-w-0" data-testid="results-v2-section">
        <ResultsOverview
          slots={slots}
          participants={participants}
          insights={insights}
          onExit={() => setOverviewOpen(false)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-3 min-w-0" data-testid="results-v2-section">
      <WinnerBanner slots={slots} insights={insights} onFinalize={onFinalize} />
      <SlotFilterChips
        slots={slots}
        insights={insights}
        view={slotView}
        onChange={setSlotView}
        onOpenOverview={
          // Vue d'ensemble (bird's-eye) dès 12 slots : à partir de là la
          // table scrolle horizontalement sur mobile et desktop compact.
          slots.length >= 12 ? () => setOverviewOpen(true) : undefined
        }
        topDay={topDay}
        topN={TOP_N}
      />
      <div className="flex items-center justify-between gap-3 flex-wrap min-w-0">
        <div className="flex items-center gap-4 flex-wrap min-w-0">
          <h2 className="text-lg font-bold text-foreground">
            {t("polls:available")}
          </h2>
          <VoteGlyphLegend />
        </div>
        <div className="-mx-1 min-w-0 w-full sm:w-auto overflow-x-auto sm:overflow-visible">
          <ResultsModeTabs mode={mode} onChange={setMode} />
        </div>
      </div>
      {mode === "consensus" && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground">
            {t("polls:results_refused_by_at_least")}
          </span>
          <ThresholdSegmented threshold={threshold} onChange={setThreshold} />
          <span className="text-xs text-muted-foreground">
            {t("polls:results_voters_label")}
          </span>
        </div>
      )}
      {mode === "blockers" && nearMisses.length === 0 && (
        <div
          className="rounded-md border border-success/40 bg-success/[0.06] px-3 py-2 text-sm text-success"
          data-testid="blockers-empty-banner"
        >
          {t("polls:results_no_blockers")}
        </div>
      )}
      {mode === "blockers" && nearMisses.length > 0 && (
        <div
          className="grid gap-3 sm:grid-cols-2"
          data-testid="blocker-callouts"
        >
          {nearMisses.map((nm) => {
            const s = slotById.get(nm.slotId);
            if (!s) return null;
            return (
              <BlockerCallout
                key={nm.slotId}
                nearMiss={nm}
                slot={s}
                isShared={share.isShared}
                onCopyLink={(id) => {
                  void copyLinkFor(id);
                }}
              />
            );
          })}
        </div>
      )}
      <ResultsTable
        mode={mode}
        slots={slots}
        participants={orderedParticipants}
        insights={insights}
        visibleSlotIds={combinedVisibleSlotIds}
        highlightedSlotIds={
          mode === "blockers"
            ? new Set(nearMisses.map((nm) => nm.slotId))
            : undefined
        }
        dimmedSlotIds={
          mode === "blockers"
            ? new Set(
                slots
                  .filter(
                    (sl) =>
                      sl.id !== insights.winnerSlotId &&
                      !nearMisses.some((nm) => nm.slotId === sl.id),
                  )
                  .map((sl) => sl.id),
              )
            : undefined
        }
      />
      {mode === "consensus" && hiddenCount > 0 && (
        <ConsensusBanner
          hiddenCount={hiddenCount}
          onShowAll={() => setThreshold(Number.POSITIVE_INFINITY)}
        />
      )}
      {mode === "engagement" && engagement && (
        <PendingParticipants
          participants={engagement.pending}
          isShared={share.isShared}
          onCopyLink={(id) => {
            void copyLinkFor(id);
          }}
          onCopyShareLink={
            engagement.pending.length > 1
              ? () => {
                  void copyShareLink();
                }
              : undefined
          }
        />
      )}
    </div>
  );
};
